
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import hostlink from '../Hostlink/hostlink';
import * as XLSX from 'xlsx';
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    StyleSheet
} from '@react-pdf/renderer';

const Rpt_FinishingReport = () => {

    const usershopid = localStorage.getItem('MYPCKARTUSER')
        ? JSON.parse(localStorage.getItem('MYPCKARTUSER'))[0].id
        : '0';

    const today = new Date();

    const [data, setData] = useState([]);
    const [FebData, setFebData] = useState([]);
    const [WData, setWData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [frmdata, setfrmdata] = useState({
        fromdate: "2024-01-01",
        todate: today.toISOString().split('T')[0],
        deptcode: 0,
        status: 1
    });

    const [itemdetail, setitemdetail] = useState({
        Washername: '',
        Washercode: 0,
        fabname: '',
        fabcode: 0
    });

    const [lotSearch, setLotSearch] = useState('');

    const { fromdate, todate, status } = frmdata;
    const { fabcode, Washercode } = itemdetail;

    const tableRef = useRef(null);

    // =====================================================
    // FORMAT NUMBER
    // =====================================================

    const formatNumber = (value, decimals = 2) => {

        const number = Number(value);

        if (!Number.isFinite(number)) {
            return decimals === 0 ? '0' : '0.00';
        }

        return number.toLocaleString('en-IN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // =====================================================
    // FABRICATOR / PRESSMAN CHANGE
    // =====================================================

    const onChangevalueFab = (fabcode, fabname) => {

        setitemdetail({
            ...itemdetail,
            fabcode,
            fabname
        });
    };

    // =====================================================
    // WASHER CHANGE
    // =====================================================

    const onChangevalueWasher = (Washercode, Washername) => {

        setitemdetail({
            ...itemdetail,
            Washercode,
            Washername
        });
    };

    // =====================================================
    // FORM CHANGE
    // =====================================================

    const onChangevalue = (e) => {

        const { name, value } = e.target;

        if (
            name === 'fromdate' ||
            name === 'todate' ||
            name === 'status'
        ) {
            setfrmdata({
                ...frmdata,
                [name]: value
            });
            return;
        }

        const sanitizedValue = value.replace(
            /['"!`@#$%^&*+{}|;:,.<>?=]/g,
            ''
        );

        setfrmdata({
            ...frmdata,
            [name]: sanitizedValue
        });
    };

    // =====================================================
    // LOAD DATA
    // =====================================================

    const loaddata = async () => {

        if (!fromdate || !todate) {
            alert('Please select From Date and To Date.');
            return;
        }

        if (fromdate > todate) {
            alert('From Date cannot be greater than To Date.');
            return;
        }

        try {

            setLoading(true);

            const billResponse = await axios.get(
                `${hostlink}/fatchFinishReport/${usershopid}/${fromdate}/${todate}/${fabcode}/${Washercode}`
            );

            const billData = Array.isArray(billResponse.data)
                ? billResponse.data
                : [];

            const filteredData = billData.filter((res) => {

                const totalQuantity =
                    Number(res?.stkInfab || 0) +
                    Number(res?.inQty || 0);

                if (Number(status) === 0) {
                    return true;
                }

                if (Number(status) === 1) {
                    return totalQuantity !== 0;
                }

                if (Number(status) === 2) {
                    return Number(res?.inQty || 0) < 0;
                }

                return false;
            });

            setData(filteredData);

        } catch (error) {

            console.log('Data Not Found...', error);
            setData([]);

        } finally {

            setLoading(false);

        }
    };

    // =====================================================
    // LOAD PRESSMAN MASTER
    // =====================================================

    useEffect(() => {

        axios
            .get(`${hostlink}/${usershopid}/FinishingMasCont`)
            .then((Response) => {

                setFebData(
                    Array.isArray(Response.data)
                        ? Response.data
                        : []
                );

            })
            .catch(() => {
                setFebData([]);
            });

    }, [usershopid]);

    // =====================================================
    // LOT SEARCH
    // =====================================================

    const filteredData = useMemo(() => {

        const search = lotSearch
            .trim()
            .toLowerCase();

        if (!search) {
            return data;
        }

        return data.filter((res) => {

            const lotNo =
                res?.purchaseMas?.billno ??
                res?.billno ??
                '';

            return String(lotNo)
                .toLowerCase()
                .includes(search);
        });

    }, [data, lotSearch]);

    // =====================================================
    // TOTALS
    // =====================================================

    const totals = useMemo(() => {

        const totalInQty = filteredData.reduce(
            (total, res) =>
                total + Number(res?.stkInfab || 0),
            0
        );

        const totalOutQty = filteredData.reduce(
            (total, res) =>
                total + Number(res?.inQty || 0) * -1,
            0
        );

        const totalAlter = filteredData.reduce(
            (total, res) =>
                total + Number(res?.inQty2 || 0),
            0
        );

        // Existing table calculation:
        // stkInfab + inQty - inQty2
        const totalPending = filteredData.reduce(
            (total, res) =>
                total +
                Number(res?.stkInfab || 0) +
                Number(res?.inQty || 0) -
                Number(res?.inQty2 || 0),
            0
        );

        const uniqueLots = new Set(
            filteredData.map(
                (res) => res?.purchaseMas?.billno
            )
        ).size;

        return {
            totalInQty,
            totalOutQty,
            totalAlter,
            totalPending,
            uniqueLots,
            totalRecords: filteredData.length
        };

    }, [filteredData]);

    // =====================================================
    // EXCEL
    // =====================================================

    const exportToExcel = () => {

        if (!filteredData.length) {
            alert('No data available to export.');
            return;
        }

        const ws = XLSX.utils.table_to_sheet(
            tableRef.current
        );

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            'Pressman Report'
        );

        XLSX.writeFile(
            wb,
            'Pressman Report.xlsx'
        );
    };

    // =====================================================
    // SUMMARY CARD
    // =====================================================

    const SummaryCard = ({
        icon,
        title,
        value,
        subtitle,
        iconClass
    }) => {

        return (

            <div className="col-6 col-md-4 col-xl">

                <div
                    className="card border-0 h-100"
                    style={{
                        borderRadius: '16px',
                        boxShadow:
                            '0 5px 18px rgba(0,0,0,0.07)',
                        background: '#ffffff'
                    }}
                >

                    <div className="card-body p-3">

                        <div className="d-flex justify-content-between align-items-start">

                            <div>

                                <div
                                    className="text-muted mb-1"
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {title}
                                </div>

                                <div
                                    style={{
                                        fontSize: '21px',
                                        fontWeight: '700',
                                        color: '#212529'
                                    }}
                                >
                                    {value}
                                </div>

                                <div
                                    className="text-muted mt-1"
                                    style={{
                                        fontSize: '11px'
                                    }}
                                >
                                    {subtitle}
                                </div>

                            </div>

                            <div
                                className={iconClass}
                                style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#f4f7fb',
                                    fontSize: '18px'
                                }}
                            >
                                <i className={icon}></i>
                            </div>

                        </div>

                    </div>

                </div>

            </div>
        );
    };

    // =====================================================
    // MAIN UI
    // =====================================================

    return (

        <div
            className="container-fluid py-3"
            style={{
                background: '#f6f8fb',
                minHeight: '100vh'
            }}
        >

            {/* =================================================
                FILTER CARD
            ================================================= */}

            <div
                className="card border-0 mb-3"
                style={{
                    borderRadius: '18px',
                    boxShadow:
                        '0 5px 20px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                }}
            >

                <div
                    className="card-body p-3 p-md-4"
                    style={{
                        background:
                            'linear-gradient(135deg,#ffffff 0%,#f8fbff 100%)'
                    }}
                >

                    <div className="row g-3 align-items-end">

                        {/* TITLE */}

                        <div className="col-12 col-xl-3">

                            <div className="d-flex align-items-center">

                                <div
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '14px',
                                        background:
                                            'linear-gradient(135deg,#0d6efd,#4dabf7)',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        boxShadow:
                                            '0 5px 15px rgba(13,110,253,0.25)'
                                    }}
                                >
                                    <i className="fa-solid fa-check-to-slot"></i>
                                </div>

                                <div>

                                    <h4
                                        className="mb-1"
                                        style={{
                                            fontWeight: '700',
                                            color: '#1f2937'
                                        }}
                                    >
                                        Pressman Report
                                    </h4>

                                    <div
                                        className="text-muted"
                                        style={{
                                            fontSize: '12px'
                                        }}
                                    >
                                        Finishing stock & pending
                                    </div>

                                </div>

                            </div>

                        </div>


                        {/* LOT SEARCH */}

                        <div className="col-12 col-md-6 col-xl-2">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                Lot No
                            </label>

                            <div className="input-group">

                                <span className="input-group-text bg-white">
                                    <i className="fa-solid fa-magnifying-glass text-primary"></i>
                                </span>

                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search Lot No..."
                                    value={lotSearch}
                                    onChange={(e) =>
                                        setLotSearch(
                                            e.target.value
                                        )
                                    }
                                    style={{
                                        fontSize: '13px',
                                        boxShadow: 'none'
                                    }}
                                />

                                {lotSearch && (

                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={() =>
                                            setLotSearch('')
                                        }
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>

                                )}

                            </div>

                        </div>


                        {/* PRESSMAN */}

                        <div className="col-12 col-md-6 col-xl-2">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                Pressman
                            </label>

                            <select
                                id="fabname"
                                value={itemdetail.fabcode || 0}
                                onChange={(e) => {

                                    const selectedIndex =
                                        e.target.selectedIndex;

                                    const selectedCode =
                                        e.target.value;

                                    const selectedName =
                                        e.target.options[
                                            selectedIndex
                                        ].text;

                                    onChangevalueFab(
                                        selectedCode,
                                        selectedName
                                    );

                                }}
                                className="form-select"
                                style={{
                                    fontSize: '13px'
                                }}
                            >

                                <option value={0}>
                                    All Pressman
                                </option>

                                {FebData.map((x) => (

                                    <option
                                        key={x.id}
                                        value={x.id}
                                    >
                                        {x.fname}
                                    </option>

                                ))}

                            </select>

                        </div>


                        {/* FROM DATE */}

                        <div className="col-6 col-md-4 col-xl-2">

                            <label
                                htmlFor="fromdate"
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                From Date
                            </label>

                            <input
                                type="date"
                                id="fromdate"
                                name="fromdate"
                                value={fromdate}
                                onChange={onChangevalue}
                                className="form-control"
                                style={{
                                    fontSize: '13px'
                                }}
                            />

                        </div>


                        {/* TO DATE */}

                        <div className="col-6 col-md-4 col-xl-2">

                            <label
                                htmlFor="todate"
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                To Date
                            </label>

                            <input
                                type="date"
                                id="todate"
                                name="todate"
                                value={todate}
                                min={fromdate}
                                onChange={onChangevalue}
                                className="form-control"
                                style={{
                                    fontSize: '13px'
                                }}
                            />

                        </div>


                        {/* STATUS */}

                        <div className="col-12 col-md-4 col-xl-1">

                            <label
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                Status
                            </label>

                            <select
                                id="roomtype"
                                name="status"
                                value={status}
                                onChange={onChangevalue}
                                className="form-select"
                                style={{
                                    fontSize: '13px'
                                }}
                            >

                                <option value={0}>
                                    All
                                </option>

                                <option value={1}>
                                    Pending
                                </option>

                                <option value={2}>
                                    Received
                                </option>

                            </select>

                        </div>


                        {/* SHOW */}

                        <div className="col-12 col-md-4 col-xl-1">

                            <button
                                type="button"
                                onClick={loaddata}
                                disabled={loading}
                                className="btn btn-primary w-100"
                                style={{
                                    borderRadius: '9px',
                                    fontWeight: '600',
                                    padding: '8px'
                                }}
                            >

                                {loading ? (

                                    <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                    ></span>

                                ) : (

                                    <>
                                        <i className="fa-solid fa-magnifying-glass-chart"></i>
                                        <span className="d-none d-xl-inline ms-1">
                                            Show
                                        </span>
                                    </>

                                )}

                            </button>

                        </div>

                    </div>


                    {/* ACTIONS */}

                    <div
                        className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3"
                        style={{
                            borderTop:
                                '1px solid #edf0f4'
                        }}
                    >

                        <div
                            className="text-muted"
                            style={{
                                fontSize: '12px'
                            }}
                        >

                            <i className="fa-solid fa-filter me-1"></i>

                            {itemdetail.fabname &&
                            itemdetail.fabname !== 'All'
                                ? `Pressman: ${itemdetail.fabname}`
                                : 'All Pressman'}

                            {lotSearch && (
                                <span className="ms-2">
                                    • Lot: <b>{lotSearch}</b>
                                </span>
                            )}

                        </div>


                        <div className="d-flex gap-2">

                            <button
                                type="button"
                                onClick={exportToExcel}
                                className="btn btn-sm btn-outline-success"
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: '600'
                                }}
                            >
                                <i className="fa-solid fa-file-excel me-1"></i>
                                Excel
                            </button>


                            <PDFDownloadLink
                                document={
                                    <PDFDocument
                                        data={filteredData}
                                        fromdate={fromdate}
                                        todate={todate}
                                    />
                                }
                                fileName="Pressman Report.pdf"
                                style={{
                                    textDecoration: 'none'
                                }}
                            >

                                {({ loading }) => (

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        disabled={loading}
                                        style={{
                                            borderRadius: '8px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <i className="fa-solid fa-file-pdf me-1"></i>

                                        {loading
                                            ? 'Preparing...'
                                            : 'PDF'}

                                    </button>

                                )}

                            </PDFDownloadLink>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                KPI CARDS
            ================================================= */}

            <div className="row g-3 mb-3">

                <SummaryCard
                    icon="fa-solid fa-layer-group"
                    title="Total Lots"
                    value={totals.uniqueLots}
                    subtitle="Unique Lot No"
                    iconClass="text-primary"
                />

                <SummaryCard
                    icon="fa-solid fa-list"
                    title="Records"
                    value={totals.totalRecords}
                    subtitle="Report entries"
                    iconClass="text-info"
                />

                <SummaryCard
                    icon="fa-solid fa-arrow-down"
                    title="In Qty"
                    value={formatNumber(totals.totalInQty)}
                    subtitle="Received at pressman"
                    iconClass="text-success"
                />

                <SummaryCard
                    icon="fa-solid fa-arrow-up"
                    title="Out Qty"
                    value={formatNumber(totals.totalOutQty)}
                    subtitle="Received back"
                    iconClass="text-warning"
                />

                <SummaryCard
                    icon="fa-solid fa-clock"
                    title="Pending"
                    value={formatNumber(totals.totalPending)}
                    subtitle="Current pending"
                    iconClass="text-danger"
                />

            </div>


            {/* =================================================
                TABLE CARD
            ================================================= */}

            <div
                className="card border-0"
                style={{
                    borderRadius: '18px',
                    boxShadow:
                        '0 5px 20px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                }}
            >

                <div className="card-header bg-white border-0 p-3">

                    <div className="d-flex justify-content-between align-items-center">

                        <div>

                            <h6
                                className="mb-1"
                                style={{
                                    fontWeight: '700',
                                    color: '#212529'
                                }}
                            >
                                <i className="fa-solid fa-table me-2 text-primary"></i>
                                Pressman Details
                            </h6>

                            <small className="text-muted">
                                {fromdate} to {todate}
                            </small>

                        </div>

                        <span
                            className="badge rounded-pill bg-primary-subtle text-primary"
                            style={{
                                padding: '8px 12px'
                            }}
                        >
                            {filteredData.length} Records
                        </span>

                    </div>

                </div>


                <div
                    style={{
                        maxHeight:
                            'calc(100vh - 390px)',
                        minHeight: '250px',
                        overflow: 'auto'
                    }}
                >

                    <table
                        className="table table-hover mb-0"
                        ref={tableRef}
                        style={{
                            minWidth: '900px'
                        }}
                    >

                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                background: '#f4f7fb'
                            }}
                        >

                            <tr>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Lot No
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Pressman Name
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Style
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Size
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    In Qty
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Out Qty
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Alter
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Pending
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="8"
                                        className="text-center py-5"
                                    >

                                        <div
                                            className="spinner-border text-primary mb-2"
                                            role="status"
                                        ></div>

                                        <div className="text-muted">
                                            Loading pressman report...
                                        </div>

                                    </td>

                                </tr>

                            ) : filteredData.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="8"
                                        className="text-center py-5"
                                    >

                                        <div
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '50%',
                                                background: '#f1f5f9',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin:
                                                    '0 auto 12px',
                                                fontSize: '24px',
                                                color: '#94a3b8'
                                            }}
                                        >
                                            <i className="fa-solid fa-inbox"></i>
                                        </div>

                                        <div
                                            style={{
                                                fontWeight: '600',
                                                color: '#475569'
                                            }}
                                        >
                                            No records found
                                        </div>

                                        <small className="text-muted">
                                            Select filters and click Show
                                        </small>

                                    </td>

                                </tr>

                            ) : (

                                filteredData.map((res, x) => {

                                    const pending =
                                        Number(
                                            res?.stkInfab || 0
                                        ) +
                                        Number(
                                            res?.inQty || 0
                                        ) -
                                        Number(
                                            res?.inQty2 || 0
                                        );

                                    const lotNo =
                                        res?.purchaseMas?.billno ??
                                        res?.billno ??
                                        '';

                                    return (

                                        <tr key={x}>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                <span
                                                    style={{
                                                        fontWeight: '700',
                                                        color: '#0d6efd'
                                                    }}
                                                >
                                                    {lotNo}
                                                </span>
                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                {res?.fabName || '-'}
                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                {res?.purchaseMas?.style || '-'}
                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                <span
                                                    className="badge bg-light text-dark"
                                                    style={{
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {res?.purchaseMas?.size || '-'}
                                                </span>
                                            </td>

                                            <td
                                                className="text-end"
                                                style={tdStyle}
                                            >
                                                {formatNumber(
                                                    res?.stkInfab
                                                )}
                                            </td>

                                            <td
                                                className="text-end"
                                                style={tdStyle}
                                            >
                                                {formatNumber(
                                                    Number(
                                                        res?.inQty || 0
                                                    ) * -1
                                                )}
                                            </td>

                                            <td
                                                className="text-end"
                                                style={tdStyle}
                                            >
                                                {formatNumber(
                                                    res?.inQty2
                                                )}
                                            </td>

                                            <td
                                                className="text-end"
                                                style={{
                                                    ...tdStyle,
                                                    fontWeight: '700',
                                                    color:
                                                        pending > 0
                                                            ? '#dc3545'
                                                            : '#198754'
                                                }}
                                            >
                                                {formatNumber(
                                                    pending
                                                )}
                                            </td>

                                        </tr>

                                    );

                                })

                            )}

                        </tbody>


                        {/* TOTAL */}

                        {filteredData.length > 0 && (

                            <tfoot
                                style={{
                                    position: 'sticky',
                                    bottom: 0,
                                    zIndex: 4
                                }}
                            >

                                <tr
                                    style={{
                                        background: '#eaf2ff'
                                    }}
                                >

                                    <td></td>
                                    <td></td>
                                    <td></td>

                                    <td
                                        className="text-end"
                                        style={{
                                            fontWeight: '700',
                                            padding: '13px'
                                        }}
                                    >
                                        Total
                                    </td>

                                    <td
                                        className="text-end"
                                        style={totalStyle}
                                    >
                                        {formatNumber(
                                            totals.totalInQty
                                        )}
                                    </td>

                                    <td
                                        className="text-end"
                                        style={totalStyle}
                                    >
                                        {formatNumber(
                                            totals.totalOutQty
                                        )}
                                    </td>

                                    <td
                                        className="text-end"
                                        style={totalStyle}
                                    >
                                        {formatNumber(
                                            totals.totalAlter
                                        )}
                                    </td>

                                    <td
                                        className="text-end"
                                        style={{
                                            ...totalStyle,
                                            color: '#dc3545'
                                        }}
                                    >
                                        {formatNumber(
                                            totals.totalPending
                                        )}
                                    </td>

                                </tr>

                            </tfoot>

                        )}

                    </table>

                </div>

            </div>

        </div>
    );
};


// =====================================================
// TABLE STYLES
// =====================================================

const thStyle = {
    padding: '13px 12px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #e2e8f0'
};

const tdStyle = {
    padding: '11px 12px',
    fontSize: '12px',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #f1f5f9'
};

const totalStyle = {
    fontWeight: '700',
    padding: '13px'
};


// =====================================================
// PDF DOCUMENT
// =====================================================

const PDFDocument = ({
    data,
    fromdate,
    todate
}) => {

    const totalInQty = data.reduce(
        (total, res) =>
            total + Number(res?.stkInfab || 0),
        0
    );

    const totalOutQty = data.reduce(
        (total, res) =>
            total + Number(res?.inQty || 0) * -1,
        0
    );

    const totalAlter = data.reduce(
        (total, res) =>
            total + Number(res?.inQty2 || 0),
        0
    );

    const totalPending = data.reduce(
        (total, res) =>
            total +
            Number(res?.stkInfab || 0) +
            Number(res?.inQty || 0) -
            Number(res?.inQty2 || 0),
        0
    );

    const pdfNumber = (value) => {

        const number = Number(value);

        if (!Number.isFinite(number)) {
            return '0.00';
        }

        return number.toFixed(2);
    };

    return (

        <Document>

            <Page
                orientation="landscape"
                size="A4"
                style={pdfStyles.page}
            >

                <View style={pdfStyles.container}>

                    <Text style={pdfStyles.title}>
                        PRESSMAN REPORT
                    </Text>

                    <Text style={pdfStyles.subtitle}>
                        From : {fromdate}    To : {todate}
                    </Text>


                    <View style={pdfStyles.table}>

                        <View
                            style={[
                                pdfStyles.tableRow,
                                pdfStyles.headerRow
                            ]}
                        >

                            <Text style={pdfStyles.headerCell}>
                                Lot No
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Pressman Name
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Style
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Size
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                In Qty
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Out Qty
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Alter
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Pending
                            </Text>

                        </View>


                        {data.map((res, x) => {

                            const pending =
                                Number(
                                    res?.stkInfab || 0
                                ) +
                                Number(
                                    res?.inQty || 0
                                ) -
                                Number(
                                    res?.inQty2 || 0
                                );

                            return (

                                <View
                                    key={x}
                                    style={pdfStyles.tableRow}
                                >

                                    <Text style={pdfStyles.cell}>
                                        {String(
                                            res?.purchaseMas?.billno ?? ''
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.partyCell}>
                                        {String(
                                            res?.fabName ?? ''
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.cell}>
                                        {String(
                                            res?.purchaseMas?.style ?? ''
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.cell}>
                                        {String(
                                            res?.purchaseMas?.size ?? ''
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(
                                            res?.stkInfab
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(
                                            Number(
                                                res?.inQty || 0
                                            ) * -1
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(
                                            res?.inQty2
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(
                                            pending
                                        )}
                                    </Text>

                                </View>

                            );

                        })}


                        <View
                            style={[
                                pdfStyles.tableRow,
                                pdfStyles.totalRow
                            ]}
                        >

                            <Text style={pdfStyles.cell}></Text>
                            <Text style={pdfStyles.cell}></Text>
                            <Text style={pdfStyles.cell}></Text>

                            <Text style={pdfStyles.totalCell}>
                                Total :
                            </Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalInQty)}
                            </Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalOutQty)}
                            </Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalAlter)}
                            </Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalPending)}
                            </Text>

                        </View>

                    </View>

                </View>

            </Page>

        </Document>
    );
};


// =====================================================
// PDF STYLES
// =====================================================

const pdfStyles = StyleSheet.create({

    page: {
        padding: 20,
        backgroundColor: '#ffffff'
    },

    container: {
        width: '100%'
    },

    title: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 6
    },

    subtitle: {
        fontSize: 9,
        textAlign: 'center',
        marginBottom: 15
    },

    table: {
        display: 'table',
        width: '100%',
        borderWidth: 1,
        borderColor: '#d1d5db'
    },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#e5e7eb',
        minHeight: 22,
        alignItems: 'center'
    },

    headerRow: {
        backgroundColor: '#eaf2ff'
    },

    totalRow: {
        backgroundColor: '#eaf2ff',
        borderBottomWidth: 0
    },

    headerCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 5,
        flex: 1,
        textAlign: 'center'
    },

    cell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'center'
    },

    partyCell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'left'
    },

    numberCell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'right'
    },

    totalCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'right'
    },

    totalNumberCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'right'
    }

});

export default Rpt_FinishingReport;

